import { validateQId } from '@exam-management/common/utils';
import {
  type QBankStructureLevelItemWithLevel,
  QBankStructureLevelCategory,
  useQBankStructure,
} from '@exam-management/qbs-provider';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, Text } from '@shared/components';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { QContentListFilterForm, QIdSearchForm, QIdSearchFormSchema } from '../../components';
import { useQContentSearchParams } from '../../hooks';

export interface QueryParam {
  name: string;
  value: unknown;
}

// TODO: move to common
const SearchSelectionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
  })
  .nullish();

const LocationSectionSchema = z.object({
  [QBankStructureLevelCategory.Part]: SearchSelectionSchema,
  [QBankStructureLevelCategory.Subject]: SearchSelectionSchema,
  [QBankStructureLevelCategory.Group]: SearchSelectionSchema,
  [QBankStructureLevelCategory.Chapter]: SearchSelectionSchema,
  [QBankStructureLevelCategory.Topic]: SearchSelectionSchema,
  [QBankStructureLevelCategory.SubTopic]: SearchSelectionSchema,
});

export const QContentListFilterSectionFormSchema = z.object({
  ...QIdSearchFormSchema.shape,
  location: LocationSectionSchema,
  category: z.string(),
});

export type QContentListFilterSectionFormInput = z.infer<
  typeof QContentListFilterSectionFormSchema
>;

function findQBankStructureLevelItemById(
  levelItems: QBankStructureLevelItemWithLevel[],
  id: string,
) {
  const levelItem = levelItems.find((item) => item.id === id);
  return {
    label: levelItem?.tag?.name ?? '',
    value: levelItem?.id ?? '',
  };
}

export default function QContentListFilterSection() {
  const [{ qIds, category, part, subject, group, chapter, topic, subtopic }, setSearchParams] =
    useQContentSearchParams();
  const { qBankStructureLevels, qBankStructureLevelItems } = useQBankStructure();
  const qBankStructureLevelWithoutFirstLevel = useMemo(
    () => qBankStructureLevels.filter((qBankStructureLevel) => qBankStructureLevel.level !== 1),
    [qBankStructureLevels],
  );

  const formInitialValues = useMemo<QContentListFilterSectionFormInput>(() => {
    return {
      qIds: qIds,
      location: {
        PART: findQBankStructureLevelItemById(qBankStructureLevelItems, part),
        SUBJECT: findQBankStructureLevelItemById(qBankStructureLevelItems, subject),
        GROUP: findQBankStructureLevelItemById(qBankStructureLevelItems, group),
        CHAPTER: findQBankStructureLevelItemById(qBankStructureLevelItems, chapter),
        TOPIC: findQBankStructureLevelItemById(qBankStructureLevelItems, topic),
        SUB_TOPIC: findQBankStructureLevelItemById(qBankStructureLevelItems, subtopic),
      },
      category: category,
    };
  }, [qIds, category, part, subject, group, chapter, topic, subtopic]);

  const formContext = useForm<QContentListFilterSectionFormInput>({
    resolver: zodResolver(QContentListFilterSectionFormSchema),
    defaultValues: formInitialValues,
  });

  const handleSubmit = (data: QContentListFilterSectionFormInput) => {
    const params: QueryParam[] = [];

    if (data.qIds) {
      const validateQIds = data.qIds.filter(validateQId) ?? [];
      params.push({ value: validateQIds, name: 'qIds' });
    }
    if (data.location && Object.keys(data.location)?.length > 0) {
      for (const [key, value] of Object.entries(data.location)) {
        // NOTE: This is just a workaround because the key is SUB_TOPIC but the url param is subtopic
        if (key === 'SUB_TOPIC') {
          params.push({ value: value?.value, name: 'subtopic' });
        } else {
          params.push({ value: value?.value, name: key.toLocaleLowerCase() });
        }
      }
    }
    if (typeof data.category === 'string') {
      params.push({ value: data.category, name: 'category' });
    }
    if (params.length > 0) {
      setSearchParams([...params, { name: 'page', value: 0 }]);
    }
  };

  const handleClearQId = () => {
    setSearchParams([
      { name: 'qIds', value: [] },
      { name: 'page', value: 0 },
    ]);
  };

  useEffect(() => {
    const subscription = formContext.watch((value, { name }) => {
      if (name === 'qIds') return;

      handleSubmit({
        qIds,
        category: value.category as string,
        location: value.location as QContentListFilterSectionFormInput['location'],
      });
    });

    return () => {
      return subscription.unsubscribe();
    };
  }, [formContext.watch, qIds]);

  return (
    <FormProvider {...formContext}>
      <Form
        onSubmit={formContext.handleSubmit(handleSubmit)}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(4),
          p: theme.spacing(4),
        })}
      >
        <Text variant='h3'>Search & Filter</Text>
        <QIdSearchForm onClear={handleClearQId} />
        <QContentListFilterForm
          qbsLevels={qBankStructureLevelWithoutFirstLevel}
          qbsLevelItems={qBankStructureLevelItems}
        />
      </Form>
    </FormProvider>
  );
}
